/**
 * Step 5: Review & Totaalscore
 *
 * Shows calculated scores and breakdown for review.
 * 3-part scoring system: Tasks (50%) + VakCompetenties (30%) + BasisCompetenties (20%) = 100 points
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEvaluation, useCalculateScores } from '../useEvaluations';
import { useWizard } from './WizardContext';
import { ScoreDisplay } from './ScoreInput';

interface Step5ReviewProps {
  evaluationId: number;
}

export function Step5Review({ evaluationId }: Step5ReviewProps) {
  const navigate = useNavigate();
  const { data: evaluation, isLoading: isLoadingEval } = useEvaluation(evaluationId);
  const { data: scoreCalc, isLoading: isLoadingCalc, refetch } = useCalculateScores(evaluationId);
  const { stepData, updateStepData, prevStep, nextStep } = useWizard();

  // Mark as reviewed and recalculate on mount
  useEffect(() => {
    if (!stepData.step5?.reviewed) {
      updateStepData('step5', { reviewed: true });
    }
    refetch();
  }, [updateStepData, stepData.step5?.reviewed, refetch]);

  const isLoading = isLoadingEval || isLoadingCalc;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Scores berekenen...</span>
      </div>
    );
  }

  // Get scores from calculation or cached evaluation
  const taskScore = scoreCalc?.taskScore ?? (evaluation?.taskScore ? parseFloat(evaluation.taskScore) : null);
  const vakCompetencyScore = scoreCalc?.vakCompetencyScore ?? (evaluation?.vakCompetencyScore ? parseFloat(evaluation.vakCompetencyScore) : null);
  const basisCompetencyScore = scoreCalc?.basisCompetencyScore ?? (evaluation?.basisCompetencyScore ? parseFloat(evaluation.basisCompetencyScore) : null);
  const totalScore = scoreCalc?.totalScore ?? (evaluation?.totalScore ? parseFloat(evaluation.totalScore) : null);

  const weights = scoreCalc?.weights ?? { task: 50, vakCompetency: 30, basisCompetency: 20 };
  const totalScoreLevel = scoreCalc?.totalScoreLevel;
  const totalScoreLabel = scoreCalc?.totalScoreLabel;

  // Dynamic colors based on score level
  const getScoreColors = (level: number | undefined) => {
    switch (level) {
      case 1: // Onvoldoende
        return {
          bg: 'bg-gradient-to-br from-red-50 to-red-100',
          border: 'border-red-200',
          icon: 'bg-red-200',
          iconText: 'text-red-600',
          text: 'text-red-700',
          score: 'text-red-900',
          label: 'text-red-600',
        };
      case 2: // Matig
        return {
          bg: 'bg-gradient-to-br from-orange-50 to-orange-100',
          border: 'border-orange-200',
          icon: 'bg-orange-200',
          iconText: 'text-orange-600',
          text: 'text-orange-700',
          score: 'text-orange-900',
          label: 'text-orange-600',
        };
      case 3: // Voldoende
        return {
          bg: 'bg-gradient-to-br from-yellow-50 to-yellow-100',
          border: 'border-yellow-200',
          icon: 'bg-yellow-200',
          iconText: 'text-yellow-600',
          text: 'text-yellow-700',
          score: 'text-yellow-900',
          label: 'text-yellow-600',
        };
      case 4: // Goed
        return {
          bg: 'bg-gradient-to-br from-lime-50 to-lime-100',
          border: 'border-lime-200',
          icon: 'bg-lime-200',
          iconText: 'text-lime-600',
          text: 'text-lime-700',
          score: 'text-lime-900',
          label: 'text-lime-600',
        };
      case 5: // Uitzonderlijk
        return {
          bg: 'bg-gradient-to-br from-green-50 to-green-100',
          border: 'border-green-200',
          icon: 'bg-green-200',
          iconText: 'text-green-600',
          text: 'text-green-700',
          score: 'text-green-900',
          label: 'text-green-600',
        };
      default: // Fallback to green
        return {
          bg: 'bg-gradient-to-br from-green-50 to-green-100',
          border: 'border-green-200',
          icon: 'bg-green-200',
          iconText: 'text-green-600',
          text: 'text-green-700',
          score: 'text-green-900',
          label: 'text-green-600',
        };
    }
  };

  const scoreColors = getScoreColors(totalScoreLevel);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-bold text-gray-900">Stap 5: Review & Totaalscore</h2>
        <p className="mt-1 text-sm text-gray-500">
          Bekijk de berekende scores voordat je doorgaat naar afspraken.
        </p>
      </div>

      {/* Score Summary Cards - 4 cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* Task Score */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 text-center">
          <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-blue-100 flex items-center justify-center">
            <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
          </div>
          <p className="text-xs text-gray-500 mb-1">Taken ({weights.task}pt)</p>
          <p className="text-2xl font-bold text-gray-900">
            {taskScore !== null ? taskScore.toFixed(1) : '—'}
          </p>
          <p className="text-xs text-gray-400">/ {weights.task}</p>
        </div>

        {/* Vak Competency Score */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 text-center">
          <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-orange-100 flex items-center justify-center">
            <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
            </svg>
          </div>
          <p className="text-xs text-gray-500 mb-1">Vakcomp. ({weights.vakCompetency}pt)</p>
          <p className="text-2xl font-bold text-gray-900">
            {vakCompetencyScore !== null ? vakCompetencyScore.toFixed(1) : '—'}
          </p>
          <p className="text-xs text-gray-400">/ {weights.vakCompetency}</p>
        </div>

        {/* Basis Competency Score */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 text-center">
          <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-purple-100 flex items-center justify-center">
            <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          </div>
          <p className="text-xs text-gray-500 mb-1">Basiscomp. ({weights.basisCompetency}pt)</p>
          <p className="text-2xl font-bold text-gray-900">
            {basisCompetencyScore !== null ? basisCompetencyScore.toFixed(1) : '—'}
          </p>
          <p className="text-xs text-gray-400">/ {weights.basisCompetency}</p>
        </div>

        {/* Total Score */}
        <div className={`${scoreColors.bg} rounded-lg border ${scoreColors.border} shadow-sm p-4 text-center`}>
          <div className={`w-10 h-10 mx-auto mb-2 rounded-full ${scoreColors.icon} flex items-center justify-center`}>
            <svg className={`w-5 h-5 ${scoreColors.iconText}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
            </svg>
          </div>
          <p className={`text-xs ${scoreColors.text} mb-1`}>Totaal</p>
          <p className={`text-3xl font-bold ${scoreColors.score}`}>
            {totalScore !== null ? totalScore.toFixed(1) : '—'}
          </p>
          <p className={`text-xs ${scoreColors.text}`}>/ 100</p>
          {totalScoreLabel && (
            <p className={`text-sm font-semibold ${scoreColors.label} mt-2 pt-2 border-t ${scoreColors.border}`}>
              {totalScoreLabel}
            </p>
          )}
        </div>
      </div>

      {/* Formula Explanation */}
      <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
        <h4 className="text-sm font-medium text-gray-700 mb-2">Berekeningsformule</h4>
        <div className="flex items-center gap-2 text-sm text-gray-600 flex-wrap">
          <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">Taken ({weights.task}pt)</span>
          <span>+</span>
          <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded">Vakcomp. ({weights.vakCompetency}pt)</span>
          <span>+</span>
          <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded">Basiscomp. ({weights.basisCompetency}pt)</span>
          <span>=</span>
          <span className="px-2 py-1 bg-green-100 text-green-700 rounded font-medium">Totaal (100pt)</span>
        </div>
      </div>

      {/* Task Score Breakdown */}
      {scoreCalc?.breakdown?.tasks && scoreCalc.breakdown.tasks.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-6 py-4 bg-blue-50 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Takenscores ({weights.task} punten)</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Taak</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Score</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Max pt</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Behaald</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {scoreCalc.breakdown.tasks.map((task) => (
                  <tr key={task.taskId}>
                    <td className="px-4 py-3 text-sm text-gray-900">{task.name}</td>
                    <td className="px-4 py-3 text-center">
                      <ScoreDisplay score={task.score} size="sm" showLabel={false} />
                    </td>
                    <td className="px-4 py-3 text-center text-sm text-gray-500">{task.maxPoints.toFixed(1)}</td>
                    <td className="px-4 py-3 text-center text-sm font-medium text-blue-700">
                      {task.earnedPoints.toFixed(1)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Epic 4 (Story 4.3, AC #2) — uitgesplitste takenblok-totaalregel bij
          >=2 gedekte tijdvakken (core_tasks_v2), zelfde uitsplitsing als
          StepKerntaken (AD-3: getoonde termen afgerond, totaal volle
          precisie). Bij 1 tijdvak (of tasks_v1) is dit veld leeg/undefined
          en toont Step5Review niets extra's (S4-gedrag ongewijzigd). */}
      {scoreCalc?.corePeriodBreakdown && scoreCalc.corePeriodBreakdown.length >= 2 && (
        <div data-testid="review-core-total-line-split" className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
          <p className="text-sm font-medium text-gray-700 mb-1">Tijdgewogen takenblok</p>
          <p className="text-sm text-gray-600">
            {scoreCalc.corePeriodBreakdown
              .map((p) => `${p.weight.toFixed(2).replace('.', ',')} × ${p.blockPoints.toFixed(1).replace('.', ',')}`)
              .join(' + ')}{' '}
            = {taskScore !== null ? taskScore.toFixed(1).replace('.', ',') : '—'} / {weights.task}
          </p>
        </div>
      )}

      {/* Competency Scores Breakdown - 2 columns */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Vak Competencies */}
        {scoreCalc?.breakdown?.vakCompetencies && scoreCalc.breakdown.vakCompetencies.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-4 py-3 bg-orange-50 border-b border-gray-200">
              <h3 className="text-base font-medium text-gray-900">Vakcompetenties ({weights.vakCompetency} punten)</h3>
            </div>
            <div className="p-4 space-y-2">
              {scoreCalc.breakdown.vakCompetencies.map((comp: { id: number; name: string; taskName: string | null; score: number; maxPoints: number; earnedPoints: number }) => (
                <div key={comp.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <div className="flex-1 min-w-0">
                    <span className="text-sm text-gray-700 block truncate">{comp.name}</span>
                    {comp.taskName && (
                      <span className="text-xs text-orange-600">→ {comp.taskName}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 ml-2">
                    <ScoreDisplay score={comp.score} size="sm" showLabel={false} />
                    <span className="text-xs text-orange-700 font-medium">{comp.earnedPoints.toFixed(1)}pt</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Basis Competencies */}
        {scoreCalc?.breakdown?.basisCompetencies && scoreCalc.breakdown.basisCompetencies.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-4 py-3 bg-purple-50 border-b border-gray-200">
              <h3 className="text-base font-medium text-gray-900">Basiscompetenties ({weights.basisCompetency} punten)</h3>
            </div>
            <div className="p-4 space-y-2">
              {scoreCalc.breakdown.basisCompetencies.map((comp: { id: number; name: string; score: number; maxPoints: number; earnedPoints: number }) => (
                <div key={comp.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <span className="text-sm text-gray-700">{comp.name}</span>
                  <div className="flex items-center gap-2">
                    <ScoreDisplay score={comp.score} size="sm" showLabel={false} />
                    <span className="text-xs text-purple-700 font-medium">{comp.earnedPoints.toFixed(1)}pt</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
        <button
          onClick={prevStep}
          className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Vorige
        </button>

        <div className="flex gap-3">
          <button
            onClick={() => navigate(`/beoordeling/${evaluationId}/pdf-preview`)}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            Export PDF
          </button>

          <button
            onClick={nextStep}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Volgende: Afspraken
          </button>
        </div>
      </div>
    </div>
  );
}
